<template>
  <div class="configuration-explorer sidebar-grid">
    <!-- ACTIONS AREA -->
    <div class="sidebar-actions">
      <div class="configuration-explorer__toolbar">
        <Button @click="refreshConfiguration" :disabled="loading || isEditMode" size="small" variant="secondary">
          <Icon name="arrow-clockwise" />
          Refresh
        </Button>
        <Button @click="clearCache" :disabled="loading || isEditMode || !selectedTenant" size="small"
          variant="secondary">
          <Icon name="trash-simple" />
          Clear Cache
        </Button>
      </div>
      <div class="configuration-explorer__section tenant">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <h3 style="margin: 0;">Tenant</h3>
          <Button size="small" variant="secondary" @click="openCreateTenant" title="Create new tenant">
            <Icon name="plus" :size="12" />
          </Button>
        </div>
        <TenantSelector ref="tenantSelectorRef" v-model="selectedTenant" />
      </div>
    </div>

    <!-- PRESETS AREA (scroll) -->
    <div class="sidebar-presets">
      <div v-if="selectedTenant && configStore.presetConfigurations.length > 0" class="configuration-explorer__section">
        <h3>Configuration</h3>
        <div class="preset-hierarchy">
          <!-- All preset levels ordered by precedence (0-1000) -->
          <div v-for="(item, index) in orderedPresetLevels" :key="item.level.key" class="preset-hierarchy-item">
            <div class="preset-item" :class="{
              'preset-item--selected': item.preset && selectedPresetIndex === configStore.presetConfigurations.indexOf(item.preset),
              'preset-item--has-overrides': item.preset?.presetInfo.hasTenantOverrides,
              'preset-item--empty': !item.preset
            }" @click="item.preset && selectPreset(configStore.presetConfigurations.indexOf(item.preset))">
              <!-- Header with icon and metadata -->
              <div class="preset-item__header">
                <Icon :name="getPresetIcon(item.level.key)" :size="16" />
                <Badge :text="item.level.key" :variant="getPresetVariant()" size="small" />
                <div class="preset-item__precedence-info">
                  <span class="precedence-value">P: {{ item.precedence }}</span>
                  <span v-if="item.preset?.presetInfo.description" class="precedence-description">
                    {{ item.preset.presetInfo.description }}
                  </span>
                </div>
                <span v-if="item.preset?.presetInfo.hasTenantOverrides" class="preset-item__override-indicator">
                  <Icon name="layers" :size="12" />
                </span>
              </div>

              <!-- Variant selection controls -->
              <div class="preset-item__variant-control" @click.stop>
                <div class="variant-select-row">
                  <div class="select-wrapper">
                    <select :disabled="!isEditMode || !item.level.mutable || loadingVariant(item.level.key)"
                      :value="selectedIds[item.level.key]" @focus="ensureLoaded(item.level.key)"
                      @change="e => onVariantChange(item.level.key, (e.target as HTMLSelectElement).value)">
                      <option value="">None</option>
                      <option v-for="opt in optionsFor(item.level.key)" :key="opt.presetId" :value="opt.presetId">{{
                        opt.name }}</option>
                      <option v-if="isEditMode && item.level.mutable" value="__create__">+ New…</option>
                    </select>
                    <Icon v-if="loadingVariant(item.level.key)" name="circle-notch" :size="14" class="select-spinner" />
                  </div>
                  <Button v-if="isEditMode && item.level.mutable" size="small" variant="secondary"
                    :disabled="loadingVariant(item.level.key)" @click="openCreate(item.level.key)">
                    <Icon name="plus" :size="12" />
                  </Button>
                </div>
                <div v-if="variantError(item.level.key)" class="variant-error">{{ variantError(item.level.key) }}</div>
              </div>
            </div>

            <!-- Flow indicator (arrow pointing down) -->
            <div v-if="index < orderedPresetLevels.length" class="preset-flow-indicator">
              <Icon name="arrow-down" :size="12" />
            </div>
          </div>

          <!-- Tenant Overrides Section -->
          <div v-if="hasTenantOverrides" class="preset-item preset-item--tenant"
            :class="{ 'preset-item--selected': configStore.isTenantOverridesSelected }" @click="selectTenantOverrides">
            <div class="preset-item__header">
              <Icon name="building-office" :size="16" />
              <Badge text="tenant" variant="info" size="small" />
              <div class="preset-item__precedence-info">
                <span class="precedence-value">P: 1000</span>
                <span v-if="selectedTenantDescription" class="precedence-description">
                  {{ selectedTenantDescription }}
                </span>
              </div>
            </div>
            {{ selectedTenantDisplayName || selectedTenant }}
          </div>
        </div>
      </div>
      <div v-else class="configuration-explorer__section empty-presets">
        <h3>Configuration</h3>
        <p class="empty-text" v-if="!selectedTenant">Select a tenant to load presets.</p>
        <p class="empty-text" v-else>No presets loaded.</p>
      </div>
    </div>

    <!-- Loading & Error overlays -->
    <!-- <div v-if="loading" class="configuration-explorer__loading">
      <ProgressBar :indeterminate="true" />
      <span>Loading configuration...</span>
    </div> -->
    <div v-if="error" class="configuration-explorer__error">
      <Icon name="warning-circle" />
      <span>{{ error }}</span>
    </div>
    <Modal ref="variantModalRef" v-if="showCreate" :title="`Create Variant - ${createLevel}`" confirmText="Create"
      cancelText="Cancel" :confirmEnabled="newVariantName.trim().length > 0" @confirm="submitCreate"
      @cancel="cancelCreate" @close="cancelCreate">
      <form @submit.prevent="submitCreate" class="variant-form">
        <div class="vf-field">
          <label for="variant-name">Name</label>
          <input id="variant-name" v-model="newVariantName" placeholder="Name" />
        </div>
        <div class="vf-field">
          <label for="variant-desc">Description</label>
          <textarea id="variant-desc" v-model="newVariantDescription" rows="3" />
        </div>
        <div v-if="creationError" class="variant-error" style="margin-top:4px;">{{ creationError }}</div>
      </form>
    </Modal>

    <!-- Create Tenant Modal -->
    <Modal ref="tenantModalRef" v-if="showCreateTenant" title="Create New Tenant" confirmText="Create"
      cancelText="Cancel" :confirmEnabled="isFormValid" @confirm="submitCreateTenant" @cancel="cancelCreateTenant"
      @close="cancelCreateTenant">
      <form @submit.prevent="submitCreateTenant" class="tenant-form">
        <div class="tf-field">
          <label for="tenant-display-name">Display Name</label>
          <input id="tenant-display-name" v-model="newTenantDisplayName"
            placeholder="Enter tenant display name (e.g., Acme Corporation)"
            title="Human-readable name for the tenant" />
        </div>
        <div class="tf-field">
          <!-- <label for="tenant-api-name">API Name (optional)</label> -->
          <!-- <input id="tenant-api-name" v-model="newTenantName"
            :placeholder="generatedSlug || 'Auto-generated from display name'" pattern="[a-z0-9-]+"
            title="API name (lowercase letters, numbers, and hyphens only)"
            :class="{ 'validation-error': nameConflictExists }" /> -->
          <div class="field-preview">
            <small class="field-hint">
              <span v-if="!newTenantName.trim() && generatedSlug">
                Preview: <code>{{ generatedSlug }}</code>
              </span>
              <span v-else-if="!newTenantName.trim()">
                Leave empty to auto-generate from display name
              </span>
              <span v-else>
                Custom API name
              </span>
            </small>
            <div v-if="validationMessage" class="validation-message" :class="{ 'error': nameConflictExists }">
              <Icon :name="nameConflictExists ? 'exclamation-triangle' : 'info-circle'" :size="12" />
              {{ validationMessage }}
            </div>
          </div>
        </div>
        <div class="tf-field">
          <label for="tenant-desc">Description</label>
          <textarea id="tenant-desc" v-model="newTenantDescription" rows="3" placeholder="Optional description" />
        </div>

        <!-- Preset Selection Section -->
        <div class="tf-section">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <label style="font-weight: 600; margin: 0;">Preset Configuration</label>
            <div style="display: flex; gap: 8px;">
              <Button v-if="selectedSourceTenant" size="small" variant="secondary" @click="copyAllPresetsFromSource"
                type="button" title="Copy all preset assignments from source tenant">
                Copy All
              </Button>
              <Button size="small" variant="secondary" @click="clearAllPresetSelections" type="button"
                title="Clear all preset selections">
                Clear All
              </Button>
            </div>
          </div>
          <p style="font-size: 0.875rem; color: var(--text-secondary); margin-bottom: 12px;">
            Configure preset assignments for the new tenant.
          </p>

          <!-- Progress indicator for configuration steps -->
          <div class="config-progress">
            <div class="progress-step" :class="{ active: !selectedSourceTenant }" @click="switchToManualMode">
              <Icon name="cog" :size="12" />
              <span>Manual Configuration</span>
            </div>
            <div class="progress-step" :class="{ active: selectedSourceTenant }" @click="switchToCopyMode">
              <Icon name="copy" :size="12" />
              <span>Copy from Tenant</span>
            </div>
          </div>

          <div class="preset-selections">
            <div v-for="level in PRESET_LEVELS" :key="level.key" class="preset-selection-row">
              <label :for="`tenant-preset-${level.key}`" class="preset-label">
                <Badge :text="level.key" variant="info" size="small" />
                <span class="preset-precedence">P: {{ level.precedence }}</span>
              </label>
              <select :id="`tenant-preset-${level.key}`" v-model="newTenantPresets[level.key]"
                :disabled="!level.mutable" class="preset-select">
                <option value="">None</option>
                <option v-for="opt in optionsFor(level.key)" :key="opt.presetId" :value="opt.presetId">{{ opt.name }}
                </option>
              </select>
            </div>

            <!-- Source Tenant Selection (only shown in copy mode) -->
            <div v-if="selectedSourceTenant" class="tenant-source-section">
              <div class="tenant-source-row">
                <label for="tenant-source" class="preset-label">
                  <Badge text="tenant" variant="info" size="small" />
                  <span class="preset-precedence">P: 1000</span>
                </label>
                <select id="tenant-source" v-model="selectedSourceTenant" @change="onSourceTenantChange"
                  class="preset-select">
                  <option value="">None - Start with empty configuration</option>
                  <option v-for="tenant in availableTenants" :key="tenant.id" :value="tenant.name">{{ tenant.displayName
                    }}{{ tenant.description ? ` - ${tenant.description}` : '' }}</option>
                </select>
              </div>

              <!-- Toggle for copying tenant overrides -->
              <div class="tenant-override-toggle">
                <label class="toggle-label">
                  <input type="checkbox" v-model="copyTenantOverrides" class="toggle-input" />
                  <span class="toggle-slider"></span>
                  <span class="toggle-text">Copy tenant inline JSON configuration</span>
                </label>
              </div>

              <!-- Copy mode explanation (full width) -->
              <div class="copy-mode-explanation">
                <Icon name="layers" :size="12" class="explanation-icon" />
                <span>
                  Select presets for the new tenant{{ copyTenantOverrides ? ' and copy tenant configuration' : '' }}
                  from <strong>"{{ selectedSourceTenant }}"</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="tenantCreationError" class="tenant-error" style="margin-top:12px;">
          {{ tenantCreationError }}
        </div>
      </form>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted } from 'vue'
import { useConfigurationStore } from '@/stores/configuration'
import TenantSelector from '@/components/molecules/TenantSelector.vue'
import Button from '@/components/atoms/Button.vue'
import Modal from '@/components/atoms/Modal.vue'
import Icon from '@/components/atoms/Icon.vue'
import Badge from '@/components/atoms/Badge.vue'
// @ts-expect-error - ProgressBar is used in template
import ProgressBar from '@/components/atoms/ProgressBar.vue'
import { getPresetIcon } from '@/utils/presetIcons'
import { configurationService } from '@/services/api'
import { generateSlug, checkNameExists, ensureUniqueSlug } from '@/utils/slugGenerator'
import type { Tenant } from '@/types/configuration'

const configStore = useConfigurationStore()

// Component refs
const tenantSelectorRef = ref<InstanceType<typeof TenantSelector> | null>(null)

// Tenant data state
const tenants = ref<Tenant[]>([])

// Load tenants on mount
onMounted(async () => {
  try {
    tenants.value = await configurationService.getTenants()
  } catch (error) {
    console.error('Failed to load tenants for display names:', error)
  }
})

// Computed
const selectedTenant = computed({
  get: () => configStore.editorState.selectedTenant,
  set: (value) => configStore.setSelectedTenant(value || '')
})

const selectedPresetIndex = computed({
  get: () => configStore.selectedPresetIndex,
  set: (value) => configStore.setSelectedPresetIndex(value)
})

const isEditMode = computed(() => configStore.isEditMode)
const loading = computed(() => configStore.loading)
const error = computed(() => configStore.error)

// Get display name for selected tenant
const selectedTenantDisplayName = computed(() => {
  if (!selectedTenant.value) return undefined
  const tenant = tenants.value.find(t => t.name === selectedTenant.value)
  return tenant?.displayName
})

// Get description for selected tenant
const selectedTenantDescription = computed(() => {
  if (!selectedTenant.value) return undefined
  const tenant = tenants.value.find(t => t.name === selectedTenant.value)
  return tenant?.description
})

// Check if tenant overrides section should be shown
// Always show when we have a resolved configuration so users can create tenant overrides
const hasTenantOverrides = computed(() => {
  const config = configStore.currentConfiguration
  if (!config) return false

  // Show tenant overrides section if:
  // 1. We have a resolved configuration (with presetSummary), OR
  // 2. There's existing tenant configuration data
  const hasResolvedConfig = 'presetSummary' in config && Array.isArray((config as any).presetSummary)
  const hasExistingTenantConfig = ('tenantConfiguration' in config && (config as any).tenantConfiguration != null)
  return hasResolvedConfig || hasExistingTenantConfig
})

// Variant selection state
const PRESET_LEVELS = configStore.PRESET_LEVELS

// Create a complete list of all preset levels ordered by precedence
// This shows all levels whether they have presets assigned or not
const orderedPresetLevels = computed(() => {
  const loadedPresets = configStore.presetConfigurations
  const presetsByType = new Map(loadedPresets.map(p => [p.presetInfo.presetType, p]))

  return PRESET_LEVELS
    .map(level => ({
      level,
      preset: presetsByType.get(level.key),
      precedence: level.precedence
    }))
    .sort((a, b) => a.precedence - b.precedence)
})
const selectedVariants = computed(() => configStore.selectedVariantsByLevel)
// Local reactive map to avoid relying on computed setter (immediate display after async loads)
const selectedIds = ref<Record<string, string>>({})
const syncSelectedIds = () => {
  const m: Record<string, string> = {}
  PRESET_LEVELS.forEach(l => {
    const pid = selectedVariants.value[l.key]?.presetId
    m[l.key] = pid != null ? String(pid) : ''
  })
  selectedIds.value = m
}
watch(selectedVariants, () => syncSelectedIds(), { deep: true })
syncSelectedIds()
const optionsFor = (levelKey: string) => configStore.availableVariantsByLevel[levelKey] || []
const loadingVariant = (levelKey: string) => !!configStore.loadingVariants[levelKey]
const variantError = (levelKey: string) => configStore.variantErrors[levelKey]

// Variant creation modal state
const createLevel = ref<string | null>(null)
const showCreate = ref(false)
const newVariantName = ref('')
const newVariantDescription = ref('')
const creationError = ref<string | null>(null)
const variantModalRef = ref<any>(null)

const ensureLoaded = (levelKey: string) => {
  configStore.loadVariantsForLevel(levelKey)
}
const onVariantChange = (levelKey: string, value: any) => {
  if (value === '__create__') {
    openCreate(levelKey)
    // Reset select to committed until creation finishes
    return
  }
  if (value === '') {
    // User selected "None" - clear the variant selection
    configStore.selectVariantForLevel(levelKey, null)
    selectedIds.value[levelKey] = ''
    return
  }
  configStore.selectVariantForLevel(levelKey, Number(value))
  selectedIds.value[levelKey] = String(value)
}
const openCreate = (levelKey: string) => {
  createLevel.value = levelKey
  ensureLoaded(levelKey)
  newVariantName.value = ''
  newVariantDescription.value = ''
  creationError.value = null
  showCreate.value = true
}
const submitCreate = async () => {
  if (!createLevel.value || !newVariantName.value.trim()) {
    creationError.value = 'Name required'
    return
  }
  try {
    creationError.value = null
    await configStore.createVariantForLevel(createLevel.value, newVariantName.value.trim(), newVariantDescription.value.trim() || undefined)
    syncSelectedIds()
    showCreate.value = false
    createLevel.value = null
  } catch (e: any) {
    creationError.value = e.message || 'Failed to create variant'
  }
}
const cancelCreate = () => {
  showCreate.value = false
  createLevel.value = null
}

watch(showCreate, async (v) => {
  if (v) {
    await nextTick()
    variantModalRef.value?.showModal?.()
  }
})

// Tenant creation modal state
const showCreateTenant = ref(false)
const newTenantDisplayName = ref('')
const newTenantName = ref('')
const newTenantDescription = ref('')
const newTenantPresets = ref<Record<string, number | string>>({})
const selectedSourceTenant = ref<string>('')
const copySourcePresetAssignments = ref<boolean>(false)
const copyTenantOverrides = ref<boolean>(false)
const tenantCreationError = ref<string | null>(null)
const tenantModalRef = ref<any>(null)
const availableTenants = ref<Array<{ id: number; name: string; displayName: string; description?: string }>>([])

// Computed properties for slug generation and validation
const generatedSlug = computed(() => {
  if (!newTenantDisplayName.value.trim()) return ''
  return generateSlug(newTenantDisplayName.value.trim())
})

const previewApiName = computed(() => {
  // If user provided custom name, use that; otherwise show generated slug
  return newTenantName.value.trim() || generatedSlug.value
})

const existingTenantNames = computed(() => {
  return availableTenants.value.map(t => t.name)
})

const nameConflictExists = computed(() => {
  const apiName = previewApiName.value
  if (!apiName) return false
  return checkNameExists(apiName, existingTenantNames.value)
})

const suggestedUniqueName = computed(() => {
  if (!nameConflictExists.value) return ''
  const apiName = previewApiName.value
  return ensureUniqueSlug(apiName, existingTenantNames.value)
})

const validationMessage = computed(() => {
  if (!previewApiName.value) return ''

  if (nameConflictExists.value) {
    return `Name "${previewApiName.value}" already exists. Suggested: "${suggestedUniqueName.value}"`
  }

  return ''
})

const isFormValid = computed(() => {
  return newTenantDisplayName.value.trim().length > 0 && !nameConflictExists.value
})

const loadAvailableTenants = async () => {
  try {
    const tenants = await configurationService.getTenants()
    availableTenants.value = tenants.filter(t => t.isActive)
  } catch (e) {
    console.error('Failed to load tenants:', e)
  }
}

const openCreateTenant = async () => {
  // Load available tenants for selection
  await loadAvailableTenants()

  // Initialize preset selections from current tenant's committed variants
  const presetSelections: Record<string, number | string> = {}
  PRESET_LEVELS.forEach(level => {
    const committedVariant = configStore.committedVariantsByLevel[level.key]
    presetSelections[level.key] = committedVariant?.presetId ?? ''
  })

  newTenantDisplayName.value = ''
  newTenantName.value = ''
  newTenantDescription.value = ''
  newTenantPresets.value = presetSelections
  selectedSourceTenant.value = ''
  copySourcePresetAssignments.value = false
  copyTenantOverrides.value = false
  tenantCreationError.value = null
  showCreateTenant.value = true
}

const clearAllPresetSelections = () => {
  const cleared: Record<string, number | string> = {}
  PRESET_LEVELS.forEach(level => {
    cleared[level.key] = ''
  })
  newTenantPresets.value = cleared
}

const copyAllPresetsFromSource = async () => {
  if (!selectedSourceTenant.value) return

  // Load source tenant presets and copy them to current selections
  await loadSourceTenantPresets()
}

const onSourceTenantChange = async () => {
  if (!selectedSourceTenant.value) {
    copySourcePresetAssignments.value = false
    return
  }

  // Automatically load source tenant presets when a source tenant is selected
  await loadSourceTenantPresets()
}

const loadSourceTenantPresets = async () => {
  if (!selectedSourceTenant.value) return

  try {
    // Load the source tenant's configuration to get preset assignments for UI display
    const sourceConfig = await configurationService.getResolvedConfiguration(selectedSourceTenant.value)

    // Clear current selections
    const newSelections: Record<string, number | string> = {}
    PRESET_LEVELS.forEach(level => {
      newSelections[level.key] = ''
    })

    // Copy preset assignments from source tenant if they exist (for UI display only - backend will handle actual copying)
    if (sourceConfig.presetSummary && Array.isArray(sourceConfig.presetSummary)) {
      sourceConfig.presetSummary.forEach((summary: any) => {
        if (summary.presetType && summary.presetId) {
          newSelections[summary.presetType] = summary.presetId
        }
      })
    }

    // Ensure all variants are loaded for each level so dropdowns show the options
    for (const level of PRESET_LEVELS) {
      ensureLoaded(level.key)
    }

    newTenantPresets.value = newSelections
  } catch (e: any) {
    console.error('Failed to load source tenant configuration:', e)
    tenantCreationError.value = `Failed to load configuration from "${selectedSourceTenant.value}": ${e.message}`
  }
}

// Watch for changes to the copy mode toggle
watch(copySourcePresetAssignments, async (copyMode) => {
  if (!selectedSourceTenant.value) return

  if (copyMode) {
    // Load and display source tenant presets
    await loadSourceTenantPresets()
  } else {
    // Restore the original preset selections from current tenant
    const presetSelections: Record<string, number | string> = {}
    PRESET_LEVELS.forEach(level => {
      const committedVariant = configStore.committedVariantsByLevel[level.key]
      presetSelections[level.key] = committedVariant?.presetId ?? ''
    })
    newTenantPresets.value = presetSelections
  }
})

// Progress step switching functions
const switchToManualMode = () => {
  selectedSourceTenant.value = ''
  copySourcePresetAssignments.value = false
  copyTenantOverrides.value = false

  // Restore the original preset selections from current tenant
  const presetSelections: Record<string, number | string> = {}
  PRESET_LEVELS.forEach(level => {
    const committedVariant = configStore.committedVariantsByLevel[level.key]
    presetSelections[level.key] = committedVariant?.presetId ?? ''
  })
  newTenantPresets.value = presetSelections
}

const switchToCopyMode = async () => {
  // If no source tenant is selected, auto-select the first available tenant
  if (!selectedSourceTenant.value && availableTenants.value.length > 0) {
    selectedSourceTenant.value = availableTenants.value[0].name
  }

  // Allow individual preset selections in copy mode (don't auto-copy all presets)
  copySourcePresetAssignments.value = false

  // Load source tenant presets if a tenant is selected
  if (selectedSourceTenant.value) {
    await loadSourceTenantPresets()
  }
}

const submitCreateTenant = async () => {
  if (!newTenantDisplayName.value.trim()) {
    tenantCreationError.value = 'Display name is required'
    return
  }

  // Validate custom API name format if provided (lowercase, numbers, hyphens only)
  if (newTenantName.value.trim() && !/^[a-z0-9-]+$/.test(newTenantName.value.trim())) {
    tenantCreationError.value = 'API name must contain only lowercase letters, numbers, and hyphens'
    return
  }

  try {
    tenantCreationError.value = null

    // Build preset assignments object (only include non-empty selections)
    const presetAssignments: Record<string, number> = {}
    Object.entries(newTenantPresets.value).forEach(([levelKey, presetId]) => {
      if (presetId && presetId !== '') {
        presetAssignments[levelKey] = typeof presetId === 'string' ? Number(presetId) : presetId
      }
    })

    const createdTenant = await configurationService.createTenant({
      displayName: newTenantDisplayName.value.trim(),
      name: newTenantName.value.trim() || undefined,
      description: newTenantDescription.value.trim() || undefined,
      presetAssignments,
      sourceTenantName: selectedSourceTenant.value || undefined,
      copySourcePresetAssignments: copySourcePresetAssignments.value,
      copyTenantConfiguration: copyTenantOverrides.value
    })

    // Close modal first
    showCreateTenant.value = false

    // Reload tenants list to include the newly created tenant
    if (tenantSelectorRef.value) {
      await tenantSelectorRef.value.loadTenants()
    }

    // Select the newly created tenant (use the API name returned from backend)
    selectedTenant.value = createdTenant.name
  } catch (e: any) {
    tenantCreationError.value = e.message || 'Failed to create tenant'
  }
}

const cancelCreateTenant = () => {
  showCreateTenant.value = false
}

watch(showCreateTenant, async (v) => {
  if (v) {
    await nextTick()
    tenantModalRef.value?.showModal?.()
  }
})

// Methods
const selectPreset = (index: number) => {
  selectedPresetIndex.value = index
  configStore.isTenantOverridesSelected = false
  // Defer scroll until DOM updates in workspace
  requestAnimationFrame(() => {
    const target = document.querySelector<HTMLDivElement>(`#preset-section-${index}`)
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    }
  })
}

const selectTenantOverrides = () => {
  // Clear preset selection and mark tenant as selected
  selectedPresetIndex.value = -1
  configStore.isTenantOverridesSelected = true
  // Scroll to tenant overrides section
  requestAnimationFrame(() => {
    const target = document.querySelector<HTMLDivElement>('#tenant-overrides-section')
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    }
  })
}

const getPresetVariant = (): 'info' => 'info'

// Function to provide hierarchy context for precedence values
const refreshConfiguration = async () => {
  await configStore.refreshConfiguration()
}

const clearCache = async () => {
  await configStore.clearCache()
}

// getPresetIcon now provided by shared utility
</script>

<style scoped lang="scss">
/* Sidebar subgrid: actions (top auto height) + presets (scroll) */
.sidebar-grid {
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    "actions"
    "presets";
  height: 100%;
  background: var(--vscode-sideBar-background);
}

.sidebar-actions {
  grid-area: actions;
  padding: 12px 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
}

.sidebar-presets {
  grid-area: presets;
  overflow-y: auto;
  padding: 0 12px 16px;
  min-height: 0;
}

.configuration-explorer__toolbar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.configuration-explorer__section {
  margin-bottom: 20px;
}

.configuration-explorer__section h3 {
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: var(--vscode-sideBarSectionHeader-foreground);
}

.configuration-explorer__loading,
.configuration-explorer__error {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.configuration-explorer__loading {
  gap: 12px;
  color: var(--vscode-descriptionForeground);
  font-size: 14px;
}

.configuration-explorer__error {
  gap: 8px;
  padding: 12px;
  background: var(--vscode-inputValidation-errorBackground);
  border: 1px solid var(--vscode-inputValidation-errorBorder);
  border-radius: 4px;
  color: var(--vscode-inputValidation-errorForeground);
  font-size: 12px;
  margin: 16px;
  z-index: 1;
}

.empty-text {
  font-size: 12px;
  color: var(--vscode-descriptionForeground);
  margin: 0;
}

.preset-hierarchy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.preset-hierarchy-item {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.preset-flow-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 16px;
  color: var(--vscode-descriptionForeground);
  opacity: 0.6;
  font-size: 10px;

  &:hover {
    opacity: 1;
  }
}

.preset-item {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--vscode-widget-border);
  border-radius: 6px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: var(--vscode-list-hoverBackground);
    border-color: var(--vscode-focusBorder);
  }

  &--tenant {
    border-color: var(--vscode-widget-border);
    background: var(--vscode-list-hoverBackground);
  }

  &--selected {
    /* Match configuration-section--highlighted styling for visual consistency */
    outline: 1px solid var(--vscode-focusBorder);
    outline-offset: 0;
    box-shadow: 0 0 0 1px var(--vscode-focusBorder);
    background: var(--vscode-editor-background);
    border-color: var(--vscode-focusBorder);
  }

  &--empty {
    opacity: 0.6;
    cursor: default;
    background: var(--vscode-sideBar-background);
    border-style: dashed;

    &:hover {
      background: var(--vscode-sideBar-background);
      border-color: var(--vscode-widget-border);
    }
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 11px;
  }

  &__precedence-info {
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;

    .precedence-value {
      font-size: 11px;
      font-weight: 500;
      color: var(--vscode-descriptionForeground);
    }

    .precedence-description {
      font-size: 9px;
      color: var(--vscode-descriptionForeground);
      opacity: 0.8;
      font-style: italic;
    }
  }

  &__variant-control {
    margin-top: 8px;
    width: 100%;
  }

  &__override-indicator {
    display: flex;
    align-items: center;
    color: var(--vscode-charts-orange);
  }
}

/* Variant selectors */
.variant-select-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.select-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;

  select {
    flex: 1;
    padding: 4px 6px;
    font-size: 11px;
    background: var(--vscode-input-background);
    color: var(--vscode-foreground);
    border: 1px solid var(--vscode-input-border);
    border-radius: 4px;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.select-spinner {
  position: absolute;
  right: 24px;
  /* Position before the dropdown arrow */
  pointer-events: none;
  color: var(--vscode-foreground);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.variant-error {
  color: var(--vscode-inputValidation-errorForeground);
  font-size: 10px;
  margin-top: 4px;
}

/* Variant creation form inside shared modal */
.variant-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.vf-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}

.vf-field label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: var(--vscode-descriptionForeground);
}

.vf-field input,
.vf-field textarea {
  background: var(--vscode-input-background);
  color: var(--vscode-foreground);
  border: 1px solid var(--vscode-input-border);
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
}

.vf-field input:focus,
.vf-field textarea:focus {
  outline: 1px solid var(--vscode-focusBorder);
}

/* Tenant creation form inside shared modal */
.tenant-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 520px;
  /* Fixed width to prevent resizing */
  min-height: 480px;
  /* Minimum height, but allow growth */
  max-height: 80vh;
  /* Prevent getting too tall */
}

.tf-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}

.tf-field label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--vscode-descriptionForeground);
}

.tf-field input,
.tf-field textarea {
  background: var(--vscode-input-background);
  color: var(--vscode-foreground);
  border: 1px solid var(--vscode-input-border);
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 12px;
  font-family: inherit;
  transition: border-color 0.2s ease;
}

.tf-field input:focus,
.tf-field textarea:focus {
  outline: none;
  border-color: var(--vscode-focusBorder);
  box-shadow: 0 0 0 1px var(--vscode-focusBorder);
}

.tf-field input::placeholder,
.tf-field textarea::placeholder {
  color: var(--vscode-input-placeholderForeground);
  opacity: 0.8;
}

.tf-field .field-hint {
  font-size: 11px;
  color: var(--vscode-descriptionForeground);
  margin-top: 4px;
  font-style: italic;
}

.tf-field .field-preview {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}

.tf-field .field-preview code {
  background: var(--vscode-textCodeBlock-background);
  border: 1px solid var(--vscode-widget-border);
  padding: 2px 4px;
  border-radius: 3px;
  font-family: var(--vscode-editor-font-family);
  font-size: 11px;
}

.tf-field .validation-message {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  padding: 4px 6px;
  border-radius: 4px;
  background: var(--vscode-inputValidation-infoBackground);
  border: 1px solid var(--vscode-inputValidation-infoBorder);
  color: var(--vscode-inputValidation-infoForeground);
}

.tf-field .validation-message.error {
  background: var(--vscode-inputValidation-errorBackground);
  border-color: var(--vscode-inputValidation-errorBorder);
  color: var(--vscode-inputValidation-errorForeground);
}

.tf-field input.validation-error {
  border-color: var(--vscode-inputValidation-errorBorder);
  background: var(--vscode-inputValidation-errorBackground);
}

.tf-field input.validation-error:focus {
  border-color: var(--vscode-inputValidation-errorBorder);
  box-shadow: 0 0 0 1px var(--vscode-inputValidation-errorBorder);
}

.tf-section {
  padding: 12px;
  background: var(--vscode-editor-background);
  border: 1px solid var(--vscode-widget-border);
  border-radius: 6px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tf-section>p {
  margin: 0 0 8px 0;
  flex-shrink: 0;
}

.preset-selections {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Remove min-height constraint to allow natural sizing */
}

.preset-selection-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  .preset-label {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 120px;
    font-size: 11px;
    padding-top: 6px;
    /* Align with select input */
    flex-shrink: 0;
  }

  .preset-precedence {
    font-size: 10px;
    font-weight: 500;
    color: var(--vscode-descriptionForeground);
  }

  .preset-select {
    flex: 1;
    padding: 6px 8px;
    font-size: 11px;
    background: var(--vscode-input-background);
    color: var(--vscode-foreground);
    border: 1px solid var(--vscode-input-border);
    border-radius: 4px;
    cursor: pointer;
    transition: border-color 0.2s ease;
    min-width: 0;
    /* Allow shrinking */

    &:focus {
      outline: none;
      border-color: var(--vscode-focusBorder);
      box-shadow: 0 0 0 1px var(--vscode-focusBorder);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background: var(--vscode-input-background);
      color: var(--vscode-disabledForeground);
    }
  }
}

.tenant-error {
  color: var(--vscode-inputValidation-errorForeground);
  background: var(--vscode-inputValidation-errorBackground);
  border: 1px solid var(--vscode-inputValidation-errorBorder);
  padding: 10px 12px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.tenant-error::before {
  content: '⚠';
  color: var(--vscode-inputValidation-errorForeground);
  font-size: 12px;
  flex-shrink: 0;
}

.tenant-source-section {
  border-top: 1px solid var(--vscode-widget-border);
  padding-top: 16px;
  margin-top: 16px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tenant-source-section::before {
  content: '';
  position: absolute;
  top: -1px;
  left: -16px;
  right: -16px;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--vscode-widget-border), transparent);
}

.tenant-source-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  .preset-label {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 120px;
    font-size: 11px;
    padding-top: 6px;
    /* Align with select input */
    flex-shrink: 0;
  }

  .preset-precedence {
    font-size: 10px;
    font-weight: 500;
    color: var(--vscode-descriptionForeground);
  }

  .preset-select {
    flex: 1;
    padding: 6px 8px;
    font-size: 11px;
    background: var(--vscode-input-background);
    color: var(--vscode-foreground);
    border: 1px solid var(--vscode-input-border);
    border-radius: 4px;
    cursor: pointer;
    transition: border-color 0.2s ease;
    min-width: 0;
    /* Allow shrinking */

    &:focus {
      outline: none;
      border-color: var(--vscode-focusBorder);
      box-shadow: 0 0 0 1px var(--vscode-focusBorder);
    }
  }
}

.tenant-override-toggle {
  padding-left: 128px;
  /* Align with the select input */
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--vscode-foreground);
  cursor: pointer;
  user-select: none;
}

.toggle-input {
  display: none;
}

.toggle-slider {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  background: var(--vscode-input-background);
  border: 1px solid var(--vscode-input-border);
  border-radius: 9px;
  transition: all 0.2s ease;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 12px;
  height: 12px;
  background: var(--vscode-foreground);
  border-radius: 50%;
  transition: transform 0.2s ease;
}

.toggle-input:checked+.toggle-slider {
  background: var(--vscode-button-background);
  border-color: var(--vscode-button-background);
}

.toggle-input:checked+.toggle-slider::before {
  transform: translateX(14px);
  background: var(--vscode-button-foreground);
}

.toggle-text {
  flex: 1;
  line-height: 1.3;
}

.copy-mode-explanation {
  font-size: 10px;
  color: var(--vscode-descriptionForeground);
  margin: 0;
  line-height: 1.3;
  min-height: 28px;
  /* Minimum height but allow growth */
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 4px;
  background: var(--vscode-textBlockQuote-background);
  border-left: 3px solid var(--vscode-textBlockQuote-border);
  padding: 6px 8px;
  width: 100%;
  /* Full width */
}

.explanation-icon {
  color: var(--vscode-foreground);
  flex-shrink: 0;
}

.copy-mode-explanation strong {
  color: var(--vscode-foreground);
  font-weight: 600;
}

.config-progress {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px;
  background: var(--vscode-textBlockQuote-background);
  border-radius: 6px;
  border: 1px solid var(--vscode-widget-border);
  flex-shrink: 0;
}

.progress-step {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  color: var(--vscode-descriptionForeground);
  background: var(--vscode-input-background);
  border: 1px solid var(--vscode-input-border);
  transition: all 0.2s ease;
  flex: 1;
  justify-content: center;
  cursor: pointer;
  user-select: none;
}

.progress-step:hover {
  background: var(--vscode-list-hoverBackground);
  border-color: var(--vscode-focusBorder);
}

.progress-step.active {
  color: var(--vscode-button-foreground);
  background: var(--vscode-button-background);
  border-color: var(--vscode-button-background);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: default;
}

.progress-step.active:hover {
  background: var(--vscode-button-background);
  border-color: var(--vscode-button-background);
}

.progress-step span {
  white-space: nowrap;
}

.configuration-context {
  margin-top: 12px;
  padding: 8px 12px;
  background: var(--vscode-editorWidget-background);
  border: 1px solid var(--vscode-widget-border);
  border-radius: 4px;
}

.context-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--vscode-descriptionForeground);
}

.context-text {
  line-height: 1.4;
}

.context-text strong {
  color: var(--vscode-foreground);
  font-weight: 600;
}
</style>
